import { FunctionComponent, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../ui/components/loading_spinner";
import {
  AddressType,
  customer_status,
  CustomerBillingAddressModel,
  CustomerType,
  RegisteredofficeAddressModel,
  UserModel,
} from "../../../context_providers/auth/user_model";
import Icon from "@mdi/react";
import {
  mdiAccountCircle,
  mdiChevronLeft,
  mdiCurrencyRupee,
  mdiEmailFast,
  mdiLocationEnter,
  mdiMapMarker,
  mdiTruckDelivery,
} from "@mdi/js";
import NewBillingWidget from "./components/BillingAddress";
import NewDeliveryWidget from "./components/DeliveryAddress";
import CustomerSpocSection from "./components/CustomerSpocSection";
import { fetchGet, fetchPut } from "../../../service/api_client";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { urls } from "../../../utils/urls";
import ModalDialog from "../../../ui/new_components/common/modal";
import { ACTION, MODULE } from "../../../utils/enums";
import { useAuth } from "../../../context_providers/auth/authProvider";

interface CustomerCreateScreenProps {
  edit: boolean;
  data?: UserModel;
}

const ReadValue: FunctionComponent<{ value?: any }> = ({ value }) => (
  <div className="text-xs font-bold text-gray-800 break-words">
    {value !== undefined && value !== null && value !== "" ? value : "--"}
  </div>
);

const CustomerApprovalScreen: FunctionComponent<CustomerCreateScreenProps> = (
  props,
) => {
  const isUpdatePage = useRouteMatch(`${urls.customer_master}/view/:id`);
  const { id } = useParams<{ id: string }>();
  const { edit } = props;
  const { showToast } = useToast();
  const [customer, setCustomer] = useState<UserModel>();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const { isAuthorised } = useAuth();
  const [approval, setApproval] = useState(false);
  const [showCustomerApprovalModal, setShowCustomerApprovalModal] =
    useState(false);
  const [showCustomerRejectModal, setShowCustomerRejectModal] = useState(false);
  const [registeredAddress, setRegisteredAddress] =
    useState<RegisteredofficeAddressModel>();
  let url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/users/customer`;

  useEffect(() => {
    const authorised = isAuthorised({
      action: [ACTION.UPDATE, ACTION.CREATE],
      module: MODULE.customer_approval,
    });
    setApproval(authorised);
  }, []);

  useEffect(() => {
    if (customer?.registered_office_address) {
      setRegisteredAddress(customer?.registered_office_address);
    }
  }, [customer?.registered_office_address]);

  useEffect(() => {
    // Initialize form with existing data if editing
    if (edit && props.data) {
      setCustomer(props.data);
    }
  }, [edit, props.data]);

  useEffect(() => {
    if (id && isUpdatePage) {
      getData(id);
    }
  }, []);

  const getData = async (id: string) => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/users`;
      const res = await fetchGet(`${url}/${id}`);
      if (res.success) {
        const updatedData = { ...res.data, type: res.data.type }; // Update type property
        setCustomer(updatedData); // Update state with merged data
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const handleBillingAddressAdded = (
    newAddress: CustomerBillingAddressModel,
  ) => {
    setCustomer((prev) => {
      if (!prev) return prev; // Ensure customer is not undefined

      return {
        ...prev,
        billing_address: [...(prev.billing_address || []), newAddress], // Append new address
      };
    });
  };

  const handleDeliveryAddressAdded = (
    newAddress: CustomerBillingAddressModel,
  ) => {
    setCustomer((prev) => {
      if (!prev) return prev; // Ensure customer is not undefined

      return {
        ...prev,
        delivery_address: [...(prev.delivery_address || []), newAddress], // Append new address
      };
    });
  };

  const submit = async (status: string) => {
    if (status === customer_status.rejected && !customer?.reject_reason) {
      showToast({
        type: ToastType.error,
        text: "Need Customer Rejected Reason.",
      });
      return;
    }

    if (status) {
      try {
        const res = await fetchPut(`${url}/approve/${customer?.uid}`, {
          ...customer!,
          status,
        });

        if (!res.success) {
          showToast({
            type: ToastType.error,
            text: res.error ?? "Failed to update status. Try again later.",
          });
          return;
        }
        showToast({
          type: ToastType.success,
          text: "Customer Status Updated.",
        });
        history.push(urls.customer_master);
      } catch (error) {
        console.error("Error while updating customer status:", error);
        showToast({
          type: ToastType.error,
          text: "Failed to update status. Try again later.",
        });
      }
    }
  };

  return (
    <>
      {showCustomerRejectModal && (
        <ModalDialog
          show={showCustomerRejectModal}
          title={"Advance Payment Request Reject"}
          onClose={() => {
            setShowCustomerRejectModal(false);
            setCustomer({
              ...customer!,
              status: customer_status.pending,
            });
          }}
        >
          <div className="bg-white p-6 rounded-md shadow-lg w-96">
            <h2 className="text-md font-semibold mb-4">
              Do you want to change Customer Status to Rejected?
            </h2>
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-500">Reason *</label>
              <textarea
                value={customer?.reject_reason}
                onChange={(e) => {
                  setCustomer({ ...customer!, reject_reason: e.target.value });
                }}
                className="w-full px-3 py-2 border rounded-md"
                rows={4}
              ></textarea>
            </div>
            <div className="flex justify-between mt-4">
              <button
                onClick={() => {
                  setShowCustomerRejectModal(false);
                  setCustomer({
                    ...customer!,
                    status: customer_status.pending,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                onClick={() => submit(customer_status.rejected)}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
      {showCustomerApprovalModal && (
        <ModalDialog
          show={showCustomerApprovalModal}
          title={"customer Approval"}
          onClose={() => {
            setShowCustomerApprovalModal(false);
            setCustomer({
              ...customer!,
              status: customer_status?.pending!,
            });
          }}
        >
          <div className="bg-white p-6 rounded-md shadow-lg w-96">
            <h2 className="text-md font-semibold mb-4">
              Do you want to change Customer Status to Approved?
            </h2>
            <div className="flex justify-between mt-4">
              <button
                onClick={() => {
                  setShowCustomerApprovalModal(false);
                  setCustomer({
                    ...customer!,
                    status: customer_status?.pending,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                onClick={() => {
                  submit(customer_status.approved);
                }}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
      <div className="flex items-center justify-between p-2 sticky top-0 bg-myBgcolor">
        <div className="flex flex-row items-center md:flex-row">
          <button
            className="flex items-center w-fit md:p-2 hover:text-gray-700 font-bold text-lg"
            onClick={() => {
              history.goBack();
            }}
          >
            <Icon path={mdiChevronLeft} className="h-6 w-6 font-bold" />
            <span className="hidden md:inline">Back</span>
          </button>
          <div className="text-base md:text-xl font-bold border-l-4 border-myPrimaryColor p-2 md:pl-2 md:mt-4">
            {approval &&
            (customer?.status === customer_status.pending ||
              customer?.status === customer_status.updated)
              ? "Customer Approval"
              : "Customer View"}{" "}
            <span className="text-xs md:text-sm text-black">
              : {customer?.uid}
            </span>
          </div>
        </div>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          //   if (window.confirm("Are your to create new customer ?")) {
          //     createCustomer();
          //   }
        }}
        className="grid gap-2 p-2 md:p-5 w-full overflow-hidden"
      >
        <section className="flex flex-col w-full md:w-1/2 md:gap-4 rounded-md bg-white border p-3 shadow-md">
          <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
            <span className="text-myPrimaryColor">
              <Icon path={mdiAccountCircle} size={0.8} />
            </span>
            Customer Details
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
            {/* Customer Name */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="name"
                className="text-xs font-medium text-gray-700"
              >
                Customer Name
              </label>
              <ReadValue value={customer?.first_name} />
            </div>

            {/* Customer Group */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="group"
                className="text-xs font-medium text-gray-700"
              >
                Customer Group
              </label>
              <ReadValue
                value={customer?.additional_customer_details?.customer_group}
              />
            </div>

            {/* Lead Segment */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="lead_segment"
                className="text-xs font-medium text-gray-700"
              >
                Lead Segment
              </label>
              <ReadValue
                value={customer?.additional_customer_details?.lead_segment}
              />
            </div>

            {/* Customer Type */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="type"
                className="text-xs font-medium text-gray-700"
              >
                Customer Type
              </label>
              <ReadValue
                value={customer?.additional_customer_details?.customer_type}
              />
            </div>

            {/* Customer Brand */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="name"
                className="text-xs font-medium text-gray-700"
              >
                Brand Name
              </label>
              <ReadValue value={customer?.additional_customer_details?.brand} />
            </div>

            {/*Store Name*/}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="store_name"
                className="text-xs font-medium text-gray-700"
              >
                Store Name
              </label>
              <ReadValue
                value={customer?.additional_customer_details?.store_name}
              />
            </div>
          </div>
        </section>

        <section className="py-4 bg-white rounded-md p-3 shadow-md">
          <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
            <span className="text-myPrimaryColor">
              <Icon path={mdiEmailFast} size={0.8} />
            </span>
            Contact Details
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="grid gap-1">
              <label htmlFor="contact_mobile" className="text-xs">
                Contact No
              </label>

              <ReadValue value={customer?.mobile} />
            </div>
            <div className="grid gap-1">
              <label htmlFor="contact_name" className="text-xs">
                Name of Contact Person
              </label>
              <ReadValue
                value={
                  customer?.customer_contact_details?.name_of_contact_person
                }
              />
            </div>
            <div className="grid gap-1">
              <label htmlFor="contact_email_contact_person" className="text-xs">
                Email ID of Contact Person
              </label>
              <ReadValue
                value={
                  customer?.customer_contact_details?.email_id_of_contact_person
                }
              />
            </div>
            {/* Accounts SPOC hidden for now.
            <div className="grid gap-1">
              <label htmlFor="email_id_of_accounts_spoc" className="text-xs">
                Email ID of Account SPOC
              </label>
              <ReadValue
                value={
                  customer?.customer_contact_details?.email_id_of_accounts_spoc
                }
              />
            </div>
            */}
            <div className="grid gap-1">
              <label htmlFor="contact_email" className="text-xs">
                Email ID
              </label>
              <ReadValue value={customer?.customer_contact_details?.email} />
            </div>
            <div className="grid gap-1">
              <label htmlFor="designation" className="text-xs">
                Designation
              </label>
              <ReadValue
                value={
                  customer?.customer_contact_details
                    ?.designation_of_contact_person
                }
              />
            </div>
            {/* Accounts SPOC hidden for now.
            <div className="grid gap-1">
              <label htmlFor="name_ceo_director" className="text-xs">
                Name of Account SPOC
              </label>
              <ReadValue
                value={
                  customer?.customer_contact_details?.name_of_accounts_spoc
                }
              />
            </div>
            <div className="grid gap-1">
              <label htmlFor="name_account_spoc" className="text-xs">
                Designation of Accounts SPOC
              </label>
              <ReadValue
                value={
                  customer?.customer_contact_details
                    ?.designation_of_accounts_spoc
                }
              />
            </div>
            */}
            <div className="grid gap-1">
              <label htmlFor="website" className="text-xs">
                Website
              </label>
              <ReadValue value={customer?.customer_contact_details?.website} />
            </div>
            <div className="grid gap-1">
              <label
                htmlFor="contact_mobile_contact_person"
                className="text-xs"
              >
                Mobile No. of contact person
              </label>
              <ReadValue
                value={
                  customer?.customer_contact_details
                    ?.mobile_no_of_contact_person
                }
              />
            </div>

            {/* Accounts SPOC hidden for now.
            <div className="grid gap-1">
              <label htmlFor="contact_mobile_accounts_spoc" className="text-xs">
                Mobile No. of Accounts SPOC
              </label>
              <ReadValue
                value={
                  customer?.customer_contact_details?.mobile_no_of_accounts_spoc
                }
              />
            </div>
            */}
          </div>
        </section>
        <div className="grid grid-cols-1  md:grid-cols-2 lg:grid-cols-2 gap-3">
          {/* Address Start */}
          <section className="py-4 bg-white rounded-md p-3 shadow-md">
            {/* Heading */}
            <h1 className="mb-4 font-semibold text-sm md:text-base flex gap-2 items-center">
              <span className="text-myPrimaryColor">
                <Icon path={mdiLocationEnter} size={0.9} />
              </span>
              Registered Office Address
            </h1>

            {/* Address Form */}
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3">
              {/* Left Column */}
              <div className="flex flex-col gap-2">
                {/* Address Line 1 */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="address.line1"
                    className="text-xs font-medium text-gray-700"
                  >
                    Address
                  </label>
                  <ReadValue
                    value={customer?.registered_office_address?.line1}
                  />
                </div>

                {/* Address Line 2 */}
                <div className="flex flex-col gap-1">
                  <ReadValue
                    value={customer?.registered_office_address?.line2}
                  />
                </div>

                {/* Address Line 3 */}
                <div className="flex flex-col gap-1">
                  <ReadValue
                    value={customer?.registered_office_address?.line3}
                  />
                </div>

                {/* Country Selection */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="country"
                    className="text-xs font-medium text-gray-700"
                  >
                    Country
                  </label>
                  <ReadValue
                    value={customer?.registered_office_address?.country}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-2">
                {/* Right Column */}

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="state"
                    className="text-sm  font-medium text-gray-700"
                  >
                    State
                  </label>
                  <ReadValue
                    value={customer?.registered_office_address?.state}
                  />
                </div>

                {/* City */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="city"
                    className="text-xs font-medium text-gray-700"
                  >
                    City
                  </label>
                  <ReadValue
                    value={customer?.registered_office_address?.city}
                  />
                </div>

                {/* Postal Code */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="pincode"
                    className="text-xs font-medium text-gray-700"
                  >
                    Postal Code
                  </label>
                  <ReadValue
                    value={customer?.registered_office_address?.pincode}
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="py-4 bg-white rounded-md p-3 shadow-md gap-2">
            <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
              <span className="text-myPrimaryColor">
                <Icon path={mdiCurrencyRupee} size={0.7} />
              </span>
              GST Details
            </h1>

            {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2"> */}
            <div className="flex flex-col gap-2">
              {/* GST Registration */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="gst_registration"
                  className="text-xs font-medium text-gray-700"
                >
                  GST Registration
                </label>
                <ReadValue value={customer?.gst_details?.gst_registration} />
              </div>

              {/* GST Number */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="gst_no"
                  className="text-xs font-medium text-gray-700"
                >
                  GST Number
                </label>
                <ReadValue
                  value={customer?.gst || customer?.gst_details?.gst_number}
                />
              </div>

              {/* Attachments */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-700">
                  Attachments
                </label>
                {customer?.gst_details?.attachments?.length ? (
                  <div className="w-full mt-2 mx-auto">
                    {customer?.gst_details?.attachments?.map(
                      (attachment: any, index: number) => (
                        <ul
                          key={attachment.id || index}
                          className="py-2 m-2 flex justify-between items-center rounded-md text-black border-2 px-4"
                        >
                          <li className="flex items-center gap-2">
                            {attachment.type === "PDF" ? (
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500"
                              >
                                {attachment.category} (PDF)
                              </a>
                            ) : attachment.type === "Image" ? (
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500"
                              >
                                {attachment.category} (IMAGE)
                              </a>
                            ) : (
                              <span>{attachment.name || "Unknown File"}</span>
                            )}
                          </li>
                        </ul>
                      ),
                    )}
                  </div>
                ) : (
                  <div className="w-full mt-2 mx-auto">No attachments.</div>
                )}
              </div>
            </div>
          </section>
        </div>
        <>
          <section className="py-4 bg-white rounded-md p-3 shadow-md">
            <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
              <span className="text-myPrimaryColor">
                <Icon path={mdiMapMarker} size={0.8} />
              </span>
              Billing Address
            </h1>
            <NewBillingWidget
              viewOnly
              uid={customer?.uid}
              type={AddressType.billing}
              registered_address={registeredAddress}
              onBillingAddressAdded={handleBillingAddressAdded}
              setRegisteredAddress={setRegisteredAddress}
            />
          </section>
          <section className="py-4 bg-white rounded-md p-3 shadow-md">
            <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
              <span className="text-myPrimaryColor">
                <Icon path={mdiTruckDelivery} size={0.8} />
              </span>
              {customer?.spocs?.length
                ? "SPOCs & Delivery Addresses"
                : "Delivery Address"}
            </h1>
            {customer?.spocs?.length ? (
              <CustomerSpocSection
                viewOnly
                spocs={customer.spocs}
                onChange={() => {}}
              />
            ) : (
              <NewDeliveryWidget
                viewOnly
                uid={customer?.uid}
                type={AddressType.delivery}
                registered_address={registeredAddress}
                onDeliveryAddressAdded={handleDeliveryAddressAdded}
                setRegisteredAddress={setRegisteredAddress}
              />
            )}
          </section>
        </>
        <section className="py-4 bg-white rounded-md p-3 shadow-md">
          <h1 className="mb-2 font-semibold text-sm md:text-base flex gap-2 items-center">
            <span className="text-myPrimaryColor">
              <Icon path={mdiCurrencyRupee} size={0.8} />
            </span>
            Business & Sales Details
          </h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
            <div className="grid gap-1">
              <label htmlFor="contact_mobile" className="text-xs">
                PAN No
              </label>
              <ReadValue
                value={customer?.additional_customer_details?.pan_no}
              />
            </div>
            {customer?.additional_customer_details?.customer_type ===
              CustomerType.sole_proprietorship && (
              <div className="grid gap-1">
                <label htmlFor="contact_name" className="text-xs">
                  Aadhar No
                </label>
                <ReadValue
                  value={customer?.additional_customer_details?.aadhaar_no}
                />
              </div>
            )}
            <div className="grid gap-1">
              <label htmlFor="dealercode" className="text-xs">
                Dealer Code
              </label>
              <ReadValue
                value={customer?.additional_customer_details?.dealer_code}
              />
            </div>
            <div className="grid gap-1">
              <label htmlFor="email_id_of_accounts_spoc" className="text-xs">
                Sales person
              </label>
              <ReadValue
                value={customer?.additional_customer_details?.sales_person}
              />
            </div>
            <div className="grid gap-1">
              <label htmlFor="contact_email" className="text-xs">
                Relationship Manager
              </label>
              <ReadValue
                value={
                  customer?.additional_customer_details?.relationship_manager
                }
              />
            </div>
          </div>
        </section>

        {approval &&
          (customer?.status === customer_status.pending ||
            customer?.status === customer_status.updated) && (
            <div className="flex justify-end gap-5 mt-3">
              <button
                onClick={() => {
                  setShowCustomerApprovalModal(true);
                  setCustomer({
                    ...customer!,
                    status: customer_status.approved,
                  });
                }}
                className="cursor-pointer    text-white bg-myPrimaryColor px-3 py-1 rounded font-semibold"
              >
                Approve
              </button>
              <button
                onClick={() => {
                  setShowCustomerRejectModal(true);
                  setCustomer({
                    ...customer!,
                    status: customer_status.rejected,
                  });
                }}
                className="cursor-pointer    text-white bg-red-700 px-3 py-1 rounded font-semibold"
              >
                Reject
              </button>
              <button
                onClick={() => {
                  history.push(urls.customer_master);
                }}
                className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
              >
                Quit
              </button>
            </div>
          )}
        {(customer?.status === customer_status.approved ||
          customer?.status === customer_status.rejected) && (
          <div className="flex p-2 justify-end">
            <button
              onClick={() => {
                history.push(urls.customer_master);
              }}
              className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
            >
              Quit
            </button>
          </div>
        )}
      </form>{" "}
      <LoadingWidget loading={loading} />
    </>
  );
};

export default CustomerApprovalScreen;
